import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronUp, FaEdit, FaTrash } from "react-icons/fa";
import api from "../../../utils/api";
import { empty, getTranslation, showToast, showCurrencySymbol } from "../../../helper/generalHelper";
import { default_image } from "../../../utils/constants";

const fallbackImage = "https://via.placeholder.com/150";

const MenuCard = ({ menu, onDeleteSuccess, view = "grid" }) => {
  const [showChildren, setShowChildren] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const t = getTranslation(menu.translations);

  const imageUrl =
    menu.menuImages?.[0] ||
    default_image ||
    fallbackImage;

  const handleEdit = () => {
    const isStaff = window.location.pathname.includes('/restaurant-staff');
    const editPath = isStaff ? `/restaurant-staff/employee/menus/edit?id=${menu.id}` : `/restaurant/menus/edit?id=${menu.id}`;
    navigate(editPath);
  };

  const handleDelete = async (id) => {
    if (!id) {
      alert("Invalid item ID.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this menu?"
    );
    if (!confirmed) return;

    setLoading(true);
    const isStaff = window.location.pathname.includes('/restaurant-staff');
    const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
    const res = await api.delete(`${apiPrefix}/restaurant/menu/delete?id=${id}`);
    showToast(res);
    if (res.success && onDeleteSuccess) onDeleteSuccess(id);

    setLoading(false);
  };

  if (view === "list") {
    return (
      <React.Fragment>
        <tr className="hover:bg-gray-50 transition cursor-pointer" onClick={handleEdit}>
          <td className="px-6 py-4">
            <div className="flex items-center">
              <div className="h-12 w-12 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden mr-4">
                <img
                  src={imageUrl}
                  alt={t.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div>
                <div className="text-sm font-bold text-gray-900">{t.title || "Untitled"}</div>
                {/* <div className="text-xs text-gray-500 mt-1">SKU: {menu.sku}</div> */}
              </div>
            </div>
          </td>
          <td className="px-6 py-4">
            <div className="text-sm font-bold text-gray-900">{showCurrencySymbol(menu.price)}</div>
            <div className="text-xs text-gray-500 line-through">{showCurrencySymbol(menu.mrp)}</div>
          </td>
          <td className="px-6 py-4">
            <div className="text-sm font-medium text-gray-900">{menu.quantity}</div>
          </td>
          <td className="px-6 py-4 text-center">
            <div className="flex justify-center items-center gap-3">
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(menu.id); }}
                title="Delete"
                disabled={loading}
                className="text-red-600 hover:text-red-900 p-1 rounded"
              >
                <FaTrash size={16} />
              </button>
              {menu.childMenus?.length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowChildren(!showChildren); }}
                  className="text-gray-500 hover:text-gray-900 p-1 rounded"
                >
                  {showChildren ? <FaChevronUp size={16} /> : <FaChevronDown size={16} />}
                </button>
              )}
            </div>
          </td>
        </tr>
        
        {/* Child Menus Expanded inside Table */}
        {showChildren && menu.childMenus?.length > 0 && (
          <tr className="bg-gray-50 border-t border-gray-100">
            <td colSpan="4" className="p-0">
              <div className="px-6 py-4">
                <h4 className="text-sm font-semibold text-gray-600 mb-3 border-b pb-2">Variations for {t.title}</h4>
                <div className="space-y-2">
                  {menu.childMenus.map((child, index) => {
                    const childT = getTranslation(child.translations);
                    return (
                      <div key={child.id || index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="flex items-center gap-4">
                          <img src={child.menuImages?.[0] || fallbackImage} className="w-12 h-12 rounded object-cover border" alt="" />
                          <div>
                            <p className="font-semibold text-gray-800">{childT.title}</p>
                            <div className="flex gap-4 text-xs text-gray-500 mt-1">
                              <span><strong className="text-gray-700">Price:</strong> {showCurrencySymbol(child.price)}</span>
                              <span className="line-through"><strong className="text-gray-700">MRP:</strong> {showCurrencySymbol(child.mrp)}</span>
                            </div>
                          </div>
                        </div>
                        <button onClick={() => handleDelete(child.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"><FaTrash /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  }

  return (
    <div className="border rounded-lg shadow-md bg-white overflow-hidden transition hover:shadow-lg relative cursor-pointer" onClick={handleEdit}>
      {/* Delete Button (Parent) */}
      <div className="absolute top-1 left-1 px-2 py-1 flex gap-2 z-10 drop-shadow-[0_0_6px_white] bg-white rounded-md">
        <button
          onClick={(e) => { e.stopPropagation(); handleDelete(menu.id); }}
          title="Delete"
          disabled={loading}
          className="p-1 text-red-600 hover:text-red-800"
        >
          <FaTrash />
        </button>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Image */}
        <img
          src={imageUrl}
          alt={t.title}
          className="w-full md:w-48 h-48 object-cover border-r"
        />

        {/* Content */}
        <div className="p-4 flex-1 space-y-2">
          {/* Title + SKU */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold">{t.title || "Untitled"}</h3>
              {/* <p className="text-sm text-gray-500">SKU: {menu.sku}</p> */}
            </div>

            {menu.childMenus?.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowChildren(!showChildren); }}
                className="text-gray-600 hover:text-black transition"
              >
                {showChildren ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            )}
          </div>

          {/* Description */}
          {t.shortDescription && (
            <p className="text-sm text-gray-700">{t.shortDescription}</p>
          )}

          {/* Price Info */}

          {empty(menu.childMenus?.length) && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p>
                <strong>MRP:</strong> {showCurrencySymbol(menu.mrp)}
              </p>
              <p>
                <strong>Price:</strong> {showCurrencySymbol(menu.price)}
              </p>
              <p>
                <strong>Quantity:</strong> {menu.quantity}
              </p>
              <p>
                <strong>Tax:</strong> {Number(menu.has_tax) === 1 ? `${parseFloat(menu.tax)}%` : "0%"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Child Menus */}
      {showChildren && menu.childMenus?.length > 0 && (
        <div className="bg-gray-50 border-t px-4 py-3 space-y-3">
          <h4 className="text-sm font-semibold text-gray-600">Child Menus</h4>
          {menu.childMenus.map((child, index) => {
            const childT = getTranslation(child.translations);
            const childImage = child.menuImages?.[0] || fallbackImage;
            const childId = child.id || `${menu.id}_child_${index}`;

            return (
              <div
                key={childId}
                className="flex items-start justify-between gap-3 border rounded p-3 bg-white"
              >
                <div className="flex gap-3">
                  <img
                    src={childImage}
                    alt={childT.title}
                    className="w-16 h-16 object-cover rounded border"
                  />
                  <div className="text-sm space-y-1">
                    <p className="font-medium">{childT.title || "Untitled"}</p>
                    <p className="text-gray-600 text-xs">
                      Price: {showCurrencySymbol(child.price)} | MRP: {showCurrencySymbol(child.mrp)}
                    </p>
                    <p className="text-gray-500 text-xs">
                      Tax: {Number(child.has_tax) === 1 ? `${parseFloat(child.tax)}%` : "0%"}
                    </p>
                  </div>
                </div>

                {/* Child Delete */}
                {child.id && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(child.id); }}
                    title="Delete Child"
                    className="text-red-500 hover:text-red-700"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MenuCard;
